import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LangLink, LangNavLink } from "@/components/LangLink";
import { Search, Menu, ChevronDown, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CartDrawer } from "@/components/CartDrawer";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useI18n } from "@/context/I18nContext";
import { CATEGORY_NAV, BRAND_NAV } from "@/lib/api";

const GUIDE_PATHS = [
  { path: "/shipping", key: "nav.shipping" },
  { path: "/size-guide", key: "nav.size_guide" },
  { path: "/care-guide", key: "nav.care_guide" },
];

const Logo = ({ onClick }) => (
  <LangLink to="/" onClick={onClick} className="inline-flex shrink-0 items-center gap-3 sm:gap-5" aria-label="Boomerang x Kentucky Horsewear home" data-testid="header-boomerang-logo">
    <img src="/boomerang-logo.webp" alt="Boomerang" className="h-12 w-auto sm:h-14" />
    <span className="hidden font-display text-2xl leading-none text-white/40 sm:inline">×</span>
    <img src="/kentucky-logo-desktop.png" alt="Kentucky Horsewear" className="hidden h-8 w-auto [filter:brightness(0)_invert(1)] sm:inline lg:h-10" />
  </LangLink>
);

export const Header = () => {
  const navigate = useNavigate();
  const { t, tx, lp } = useI18n();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const GUIDES = GUIDE_PATHS.map((g) => ({ path: g.path, name: t(g.key) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(lp(`/collection/saddle-pads?search=${encodeURIComponent(q.trim())}`));
      setSearchOpen(false); setMobileOpen(false);
    }
  };

  const navLink = "whitespace-nowrap px-3 py-2 text-[12px] font-medium uppercase tracking-[0.14em] transition-colors duration-200";

  return (
    <header
      className={`sticky top-0 z-50 bg-[hsl(var(--brand-navy))]/95 text-white backdrop-blur-md transition-shadow duration-300 ${scrolled ? "border-b border-white/10 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]" : "border-b border-white/5"}`}
    >
      <nav data-testid="site-header-nav" className="container-site flex h-20 items-center justify-between gap-4 lg:h-[104px]">
        <div className="flex shrink-0 items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 xl:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <LangLink to="/" onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-2">
                <img src="/boomerang-logo.webp" alt="Boomerang" className="h-8 w-auto invert-0 [filter:brightness(0)]" />
              </LangLink>
              <form onSubmit={submitSearch} className="mt-6 flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search\u2026"
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
                <Button type="submit" size="icon" aria-label="Search"><Search className="h-4 w-4" /></Button>
              </form>
              <p className="mt-7 px-1 eyebrow">{t("nav.shop_by_category")}</p>
              <div className="mt-2 flex flex-col">
                {CATEGORY_NAV.map((c) => (
                  <LangLink key={c.slug} to={`/collection/${c.slug}`} onClick={() => setMobileOpen(false)}
                    className="rounded-md px-2 py-2.5 text-sm font-medium text-foreground hover:bg-secondary">{tx(c.name)}</LangLink>
                ))}
              </div>
              <p className="mt-5 px-1 eyebrow">{t("nav.brands")}</p>
              <div className="mt-2 flex flex-col">
                {BRAND_NAV.map((b) => (
                  <LangLink key={b.slug} to={`/brand/${b.slug}`} onClick={() => setMobileOpen(false)}
                    className="rounded-md px-2 py-2.5 text-sm text-foreground hover:bg-secondary">{b.name}</LangLink>
                ))}
              </div>
              <p className="mt-5 px-1 eyebrow">{t("nav.guides")}</p>
              <div className="mt-2 flex flex-col">
                {GUIDES.map((g) => (
                  <LangLink key={g.path} to={g.path} onClick={() => setMobileOpen(false)}
                    className="rounded-md px-2 py-2.5 text-sm text-foreground hover:bg-secondary">{g.name}</LangLink>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          <Logo />
        </div>

        <div className="hidden xl:flex items-center gap-0.5" data-testid="nav-primary" onMouseLeave={() => setMegaOpen(false)}>
          <div className="relative" onMouseEnter={() => setMegaOpen(true)}>
            <button className={`${navLink} inline-flex items-center gap-1 text-white/70 hover:text-white`} data-testid="nav-shop-menu">
              {t("nav.shop")} <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
          {BRAND_NAV.slice(0, 3).map((b) => (
            <LangNavLink key={b.slug} to={`/brand/${b.slug}`}
              className={({ isActive }) => `group relative ${navLink} ${isActive ? "text-white" : "text-white/70 hover:text-white"}`}>
              {b.name}
              <span className="absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-[hsl(var(--primary))] transition-transform duration-200 group-hover:scale-x-100" />
            </LangNavLink>
          ))}
          <span className="mx-1.5 h-4 w-px bg-white/20" />
          {GUIDES.map((g) => (
            <LangNavLink key={g.path} to={g.path} className={`${navLink} text-white/55 hover:text-white`}>{g.name}</LangNavLink>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <LanguageSwitcher triggerClassName="text-white/80 hover:bg-white/10" />
          <Button variant="ghost" size="icon" data-testid="search-open-button" aria-label="Search"
            onClick={() => setSearchOpen((v) => !v)} className="hidden text-white/80 hover:bg-white/10 sm:inline-flex">
            <Search className="h-5 w-5" />
          </Button>
          <CartDrawer triggerClassName="text-white hover:bg-white/10" />
        </div>
      </nav>

      {/* Mega menu */}
      <AnimatePresence>
        {megaOpen && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }} onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}
            className="absolute inset-x-0 top-full hidden border-t border-white/10 bg-background text-foreground lg:block" data-testid="mega-menu">
            <div className="container-site grid grid-cols-3 gap-8 py-9">
              <div>
                <p className="eyebrow text-[hsl(var(--primary))]">{t("nav.shop_by_category")}</p>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1">
                  {CATEGORY_NAV.map((c) => (
                    <LangLink key={c.slug} to={`/collection/${c.slug}`} onClick={() => setMegaOpen(false)}
                      className="rounded-md px-2 py-2 text-sm text-foreground/75 hover:bg-secondary hover:text-foreground">{tx(c.name)}</LangLink>
                  ))}
                </div>
              </div>
              <div>
                <p className="eyebrow text-[hsl(var(--primary))]">{t("nav.brands")}</p>
                <div className="mt-4 flex flex-col gap-1">
                  {BRAND_NAV.map((b) => (
                    <LangLink key={b.slug} to={`/brand/${b.slug}`} onClick={() => setMegaOpen(false)}
                      className="rounded-md px-2 py-2 text-sm text-foreground/75 hover:bg-secondary hover:text-foreground">{b.name}</LangLink>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-foreground/10 bg-secondary/50 p-6">
                <p className="font-display text-xl text-foreground">{t("header.official_reseller")}</p>
                <p className="mt-1.5 text-sm text-muted-foreground">{t("header.official_reseller_desc")}</p>
                <a href="https://boomerang.ie/collections/kentucky?utm_source=kentucky.ie&utm_medium=referral&utm_campaign=mega_cta" target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] hover:underline">
                  Shop the full collection <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-[hsl(var(--brand-navy))]">
            <form onSubmit={submitSearch} className="container-site flex items-center gap-2 py-3">
              <Search className="h-4 w-4 text-white/50" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="header-search-input"
                placeholder={t("header.search_placeholder")}
                className="flex-1 bg-transparent py-1 text-sm text-white placeholder:text-white/40 outline-none" />
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
